// SPDX-License-Identifier: AGPL-3.0-or-later
//
// parley: the emoji of a soundboard sound (strand AJ,
// docs/strands/soundboard.md), drawn the same way in the panel, the
// settings list and the upload dialog. A custom emoji of the community is
// its image, a Unicode emoji its Twemoji (as everywhere else in the
// client), and a sound without one gets a note.

import styles from '@app/parley/soundboard/SoundboardEmoji.module.css';
import Emoji from '@app/features/emoji/state/Emoji';
import {buildCustomEmojiURL} from '@app/features/expressions/utils/CustomEmojiImageUrl';
import {getEmojiURL} from '@app/features/expressions/utils/EmojiUtils';
import {MusicNoteIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';

export const SoundboardEmoji = observer(
	({
		emojiId,
		emojiName,
		className,
	}: {
		emojiId: string | null;
		emojiName: string | null;
		/** Sets the size: the emoji fills the box it is given. */
		className?: string;
	}): React.ReactElement => {
		if (emojiId) {
			const emoji = Emoji.getEmojiById(emojiId);
			return (
				<img
					src={buildCustomEmojiURL({id: emojiId, animated: Boolean(emoji?.animated)})}
					alt=""
					aria-hidden="true"
					className={clsx(styles.emoji, className)}
					draggable={false}
					data-flx="parley.soundboard.soundboard-emoji.custom"
				/>
			);
		}
		const url = emojiName ? getEmojiURL(emojiName) : null;
		if (url) {
			return (
				<img
					src={url}
					alt=""
					aria-hidden="true"
					className={clsx(styles.emoji, className)}
					draggable={false}
					data-flx="parley.soundboard.soundboard-emoji.unicode"
				/>
			);
		}
		if (emojiName) {
			return (
				<span
					aria-hidden="true"
					className={clsx(styles.emoji, styles.text, className)}
					data-flx="parley.soundboard.soundboard-emoji.text"
				>
					{emojiName}
				</span>
			);
		}
		return (
			<MusicNoteIcon
				weight="fill"
				aria-hidden="true"
				className={clsx(styles.emoji, styles.fallback, className)}
				data-flx="parley.soundboard.soundboard-emoji.fallback"
			/>
		);
	},
);
